import type { Task, TaskStatus } from "./types";

export type TaskListFilters = {
  machineId?: string;
  locationId?: string;
  scheduledFrom?: string;
  scheduledTo?: string;
  overdueTo?: string;
  includeVisitMetrics?: boolean;
  statuses?: TaskStatus[];
  types?: Task["type"][];
  query?: string;
  taskIds?: string[];
  refillTaskIds?: string[];
  responseMode?: "full" | "report";
};

export const usesRoutableTaskFeed = (filters?: TaskListFilters) =>
  Boolean(
    filters?.scheduledFrom &&
      filters.scheduledTo &&
      !filters.machineId &&
      !filters.locationId &&
      !filters.taskIds?.length &&
      !filters.refillTaskIds?.length &&
      !filters.overdueTo &&
      filters.responseMode !== "report"
  );

export type DateRangeTaskScope = {
  scheduledFrom: string;
  scheduledTo: string;
  scopeKey: string;
  includeVisitMetrics?: boolean;
  responseMode?: "full" | "report";
};

export type DashboardTaskScope = DateRangeTaskScope & {
  overdueTo: string;
};

export type StatusTaskScope = {
  statuses: TaskStatus[];
  scopeKey: string;
};

export type TaskDateRangeInput = {
  startDate?: string | null;
  endDate?: string | null;
  includeVisitMetrics?: boolean;
  responseMode?: "full" | "report";
};

export type MissingTaskDateRangeScope = {
  key: string;
  startDate: string;
  endDate: string;
  includeVisitMetrics: boolean | undefined;
  responseMode?: "report";
};

const TASK_STATUSES: readonly TaskStatus[] = [
  "pending",
  "done",
  "unassigned",
  "error",
  "cancelled",
];

const DATE_RANGE_SCOPE_PATTERN =
  /^date(?::report)?(?::no-visit-metrics)?:(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;
const DASHBOARD_SCOPE_PATTERN =
  /^dashboard:(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2}):overdue\.\.(\d{4}-\d{2}-\d{2})$/;

export const taskScopeKeys = {
  all: "all",
  allReport: "all:report",
  allReportNoVisitMetrics: "all:report:no-visit-metrics",
  dateRange: (
    scheduledFrom: string,
    scheduledTo: string,
    options?: {
      includeVisitMetrics?: boolean;
      responseMode?: "full" | "report";
    }
  ) => {
    const mode = options?.responseMode === "report" ? ":report" : "";
    const metrics = options?.includeVisitMetrics === false
      ? ":no-visit-metrics"
      : "";
    return `date${mode}${metrics}:${scheduledFrom}..${scheduledTo}`;
  },
  dashboard: (scheduledFrom: string, scheduledTo: string, overdueTo: string) =>
    `dashboard:${scheduledFrom}..${scheduledTo}:overdue..${overdueTo}`,
  machine: (machineId: string) => `machine:${machineId}`,
  location: (locationId: string) => `location:${locationId}`,
  entityRange: (
    entityScope: string,
    range: TaskDateRangeInput & { allTasks?: boolean },
  ) => `${entityScope}:${range.responseMode || "full"}:${range.includeVisitMetrics === false ? "no-metrics" : "metrics"}:${range.allTasks ? "all" : `${range.startDate}..${range.endDate}`}`,
  locationDateRange: (
    locationId: string,
    scheduledFrom: string,
    scheduledTo: string,
  ) => `location:${locationId}:date:${scheduledFrom}..${scheduledTo}`,
  status: (statuses: string[]) =>
    `status:${Array.from(new Set(statuses.filter(Boolean))).sort().join(",")}`,
};

const effectiveIncludeVisitMetrics = (
  range: TaskDateRangeInput,
  options?: { includeVisitMetrics?: boolean }
) =>
  range.includeVisitMetrics === false || options?.includeVisitMetrics === false
    ? false
    : undefined;

export const normalizeTaskDateRanges = (
  ranges: TaskDateRangeInput[],
  options?: { includeVisitMetrics?: boolean }
) =>
  ranges.map((range) => ({
    startDate: range.startDate || null,
    endDate: range.endDate || null,
    includeVisitMetrics: effectiveIncludeVisitMetrics(range, options),
    ...(range.responseMode === "report"
      ? { responseMode: "report" as const }
      : {}),
  }));

export const getMissingTaskDateRangeScopes = (
  ranges: TaskDateRangeInput[],
  taskScopes: Record<string, unknown>
): MissingTaskDateRangeScope[] =>
  Array.from(
    new Map(
      ranges
        .map((range) => {
          const key =
            range.startDate && range.endDate
              ? taskScopeKeys.dateRange(range.startDate, range.endDate, {
                  includeVisitMetrics: range.includeVisitMetrics,
                  responseMode: range.responseMode,
                })
              : null;
          return key && range.startDate && range.endDate
            ? {
                key,
                startDate: range.startDate,
                endDate: range.endDate,
                includeVisitMetrics:
                  range.includeVisitMetrics === false ? false : undefined,
                ...(range.responseMode === "report"
                  ? { responseMode: "report" as const }
                  : {}),
              }
            : null;
        })
        .filter((range): range is MissingTaskDateRangeScope => Boolean(range))
        .map((range) => [range.key, range])
    ).values()
  )
    .filter(
      (range) => {
        if (
          taskScopes[range.key] ||
          (range.includeVisitMetrics === false && taskScopes[taskScopeKeys.all])
        ) return false;
        return !Object.keys(taskScopes).some((loadedKey) => {
          const match = loadedKey.match(DATE_RANGE_SCOPE_PATTERN);
          if (!match) return false;
          const loadedWithoutVisitMetrics = loadedKey.startsWith(
            "date:no-visit-metrics:"
          ) || loadedKey.startsWith(
            "date:report:no-visit-metrics:"
          );
          if (range.includeVisitMetrics !== false && loadedWithoutVisitMetrics) {
            return false;
          }
          const loadedReport = loadedKey.startsWith("date:report:");
          if (range.responseMode !== "report" && loadedReport) return false;
          return match[1] <= range.startDate && match[2] >= range.endDate;
        });
      }
    );

export const parseDateRangeTaskScope = (
  scopeKey: string
): DateRangeTaskScope | null => {
  const match = scopeKey.match(DATE_RANGE_SCOPE_PATTERN);
  return match
    ? {
        scheduledFrom: match[1],
        scheduledTo: match[2],
        scopeKey,
        ...(scopeKey.startsWith("date:no-visit-metrics:")
          || scopeKey.startsWith("date:report:no-visit-metrics:")
          ? { includeVisitMetrics: false }
          : {}),
        ...(scopeKey.startsWith("date:report:")
          ? { responseMode: "report" as const }
          : {}),
      }
    : null;
};

export const parseDashboardTaskScope = (
  scopeKey: string
): DashboardTaskScope | null => {
  const match = scopeKey.match(DASHBOARD_SCOPE_PATTERN);
  return match
    ? {
        scheduledFrom: match[1],
        scheduledTo: match[2],
        overdueTo: match[3],
        scopeKey,
      }
    : null;
};

export const parseStatusTaskScope = (
  scopeKey: string
): StatusTaskScope | null => {
  if (!scopeKey.startsWith("status:")) return null;
  const statuses = scopeKey
    .slice("status:".length)
    .split(",")
    .map((status) => status.trim())
    .filter((status): status is TaskStatus =>
      TASK_STATUSES.includes(status as TaskStatus)
    );
  return statuses.length ? { statuses, scopeKey } : null;
};
